import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Trash2, X, Upload, Users, Clock, Award, Mail, Phone, Globe, User } from 'lucide-react';
import * as supportService from '../services/supportService';
import { SupportPerson } from '../types';

const dummySupportPersons: SupportPerson[] = [
  {
    id: '1',
    regions: { tr: 'Avrupa', en: 'Europe', es: 'Europa' },
    name: { tr: 'Ahmet Yılmaz', en: 'Ahmet Yilmaz', es: 'Ahmet Yilmaz' },
    profession: { tr: 'Elektrik Mühendisi', en: 'Electrical Engineer', es: 'Ingeniero Eléctrico' },
    projects: 12,
    satisfiedCustomers: '95%',
    experience: { tr: '8 yıl', en: '8 years', es: '8 años' },
    Specialization: { tr: 'Alçak Gerilim Panoları', en: 'LV Panels', es: 'Paneles de Baja Tensión' },
    repsonseTime: '24h',
    Languages: ['tr', 'en'],
    email: 'ahmet@example.com',
    phone: '0555 000 0000',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    regions: { tr: 'Asya', en: 'Asia', es: 'Asia' },
    name: { tr: 'Fatma Demir', en: 'Fatma Demir', es: 'Fatma Demir' },
    profession: { tr: 'Makine Mühendisi', en: 'Mechanical Engineer', es: 'Ingeniera Mecánica' },
    projects: 18,
    satisfiedCustomers: '98%',
    experience: { tr: '12 yıl', en: '12 years', es: '12 años' },
    Specialization: { tr: 'Pompa Sistemleri', en: 'Pump Systems', es: 'Sistemas de Bombeo' },
    repsonseTime: '12h',
    Languages: ['tr', 'en', 'de'],
    email: 'fatma@example.com',
    phone: '0555 111 1111',
    createdAt: '2024-01-10T14:20:00Z'
  }
];

const Support: React.FC = () => {
  const [supportPersons, setSupportPersons] = useState<SupportPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<SupportPerson | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Her dil için ayrı form state'leri
  const [formRegions, setFormRegions] = useState({ tr: '', en: '', es: '' });
  const [formNames, setFormNames] = useState({ tr: '', en: '', es: '' });
  const [formProfessions, setFormProfessions] = useState({ tr: '', en: '', es: '' });
  const [formExperiences, setFormExperiences] = useState({ tr: '', en: '', es: '' });
  const [formSpecializations, setFormSpecializations] = useState({ tr: '', en: '', es: '' });
  
  // Diğer form alanları
  const [formProjects, setFormProjects] = useState<number>(0);
  const [formSatisfiedCustomers, setFormSatisfiedCustomers] = useState('');
  const [formResponseTime, setFormResponseTime] = useState('');
  const [formLanguages, setFormLanguages] = useState<string[]>([]);
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');

  // Support kişilerini API'den yükle
  const fetchSupportPersons = async () => {
    try {
      setLoading(true);
      const supportData = await supportService.getSupportPersons();
      console.log('Support persons received:', supportData);
      setSupportPersons(supportData);
    } catch (error) {
      console.error('Error fetching support persons:', error);
      toast.error('Destek kişileri yüklenirken hata oluştu');
      setSupportPersons(dummySupportPersons); // Fallback to dummy data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportPersons();
  }, []);

  const handleEdit = (person: SupportPerson) => {
    const normalizedPerson = {
      ...person,
      id: person.id || person._id,
      regions: person.regions || { tr: '', en: '', es: '' },
      name: person.name || { tr: '', en: '', es: '' },
      profession: person.profession || { tr: '', en: '', es: '' },
      experience: person.experience || { tr: '', en: '', es: '' },
      Specialization: person.Specialization || { tr: '', en: '', es: '' },
      Languages: person.Languages || []
    };
    
    setEditingPerson(normalizedPerson);
    
    // Form state'lerini güncelle
    setFormRegions({
      tr: normalizedPerson.regions.tr || '',
      en: normalizedPerson.regions.en || '',
      es: normalizedPerson.regions.es || ''
    });
    setFormNames({
      tr: normalizedPerson.name.tr || '',
      en: normalizedPerson.name.en || '',
      es: normalizedPerson.name.es || ''
    });
    setFormProfessions({
      tr: normalizedPerson.profession.tr || '',
      en: normalizedPerson.profession.en || '',
      es: normalizedPerson.profession.es || ''
    });
    setFormExperiences({
      tr: normalizedPerson.experience.tr || '',
      en: normalizedPerson.experience.en || '',
      es: normalizedPerson.experience.es || ''
    });
    setFormSpecializations({
      tr: normalizedPerson.Specialization.tr || '',
      en: normalizedPerson.Specialization.en || '',
      es: normalizedPerson.Specialization.es || ''
    });
    
    // Diğer alanlar
    setFormProjects(normalizedPerson.projects || 0);
    setFormSatisfiedCustomers(normalizedPerson.satisfiedCustomers || '');
    setFormResponseTime(normalizedPerson.repsonseTime || '');
    setFormLanguages(normalizedPerson.Languages || []);
    setFormEmail(normalizedPerson.email || '');
    setFormPhone(normalizedPerson.phone || '');
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    // Mevcut görsel varsa önizleme olarak ayarla
    const existingImage = person.profileImage || person.image;
    if (existingImage) {
      setImagePreview(existingImage);
    } else {
      setImagePreview(null);
    }
    
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleNewPerson = () => {
    setEditingPerson(null);
    
    // Form state'lerini temizle
    setFormRegions({ tr: '', en: '', es: '' });
    setFormNames({ tr: '', en: '', es: '' });
    setFormProfessions({ tr: '', en: '', es: '' });
    setFormExperiences({ tr: '', en: '', es: '' });
    setFormSpecializations({ tr: '', en: '', es: '' });
    
    setFormProjects(0);
    setFormSatisfiedCustomers('');
    setFormResponseTime('');
    setFormLanguages([]);
    setFormEmail('');
    setFormPhone('');
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    setImagePreview(null);
    setSelectedFile(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    // Blob URL'leri temizle
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    
    // Form state'lerini temizle
    setFormRegions({ tr: '', en: '', es: '' });
    setFormNames({ tr: '', en: '', es: '' });
    setFormProfessions({ tr: '', en: '', es: '' });
    setFormExperiences({ tr: '', en: '', es: '' });
    setFormSpecializations({ tr: '', en: '', es: '' });
    
    setFormProjects(0);
    setFormSatisfiedCustomers('');
    setFormResponseTime('');
    setFormLanguages([]);
    setFormEmail('');
    setFormPhone('');
    
    setShowForm(false);
    setEditingPerson(null);
    setImagePreview(null);
    setSelectedFile(null);
    setSelectedLanguage('tr');
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Görsel dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yükleyebilirsiniz');
        return;
      }

      // Eski blob URL'yi temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }

      // Yeni önizleme oluştur
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFile(file);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFile(null);
  };

  const convertImageToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDelete = async (person: SupportPerson) => {
    if (window.confirm('Bu destek kişisini silmek istediğinizden emin misiniz?')) {
      try {
        const personId = person.id || person._id;
        if (!personId) {
          toast.error('Kişi ID bulunamadı');
          return;
        }
        await supportService.deleteSupportPerson(personId);
        toast.success('Destek kişisi başarıyla silindi');
        fetchSupportPersons();
      } catch (error) {
        console.error('Error deleting support person:', error);
        toast.error('Destek kişisi silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      // Görsel varsa base64'e çevir
      let profileImageBase64 = '';
      if (selectedFile) {
        try {
          profileImageBase64 = await convertImageToBase64(selectedFile);
        } catch (error) {
          console.error('Error converting image to base64:', error);
          toast.error('Görsel işlenirken hata oluştu');
          return;
        }
      }
      
      const supportData = {
        regions: {
          tr: formRegions.tr || '',
          en: formRegions.en || '',
          es: formRegions.es || '',
        },
        name: {
          tr: formNames.tr || '',
          en: formNames.en || '',
          es: formNames.es || '',
        },
        profession: {
          tr: formProfessions.tr || '',
          en: formProfessions.en || '',
          es: formProfessions.es || '',
        },
        experience: {
          tr: formExperiences.tr || '',
          en: formExperiences.en || '',
          es: formExperiences.es || '',
        },
        Specialization: {
          tr: formSpecializations.tr || '',
          en: formSpecializations.en || '',
          es: formSpecializations.es || '',
        },
        projects: formProjects,
        satisfiedCustomers: formSatisfiedCustomers,
        repsonseTime: formResponseTime, // API'deki typo'yu koruyoruz
        Languages: formLanguages,
        email: formEmail,
        phone: formPhone,
        ...(profileImageBase64 && { profileImage: profileImageBase64 }) // Görsel varsa ekle
      };

      if (editingPerson) {
        const id = editingPerson.id || editingPerson._id;
        if (!id) {
          toast.error('Kişi ID bulunamadı');
          return;
        }
        await supportService.updateSupportPerson(id, supportData);
        toast.success('Destek kişisi başarıyla güncellendi');
      } else {
        await supportService.createSupportPerson(supportData);
        toast.success('Destek kişisi başarıyla oluşturuldu');
      }

      // Form'u kapat ve temizle
      handleCloseForm();
      
      // Support listesini yenile
      fetchSupportPersons();
    } catch (error: any) {
      console.error('Error saving support person:', error);
      toast.error('Destek kişisi kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // Dil yönetimi
  const addLanguage = (lang: string) => {
    if (lang && !formLanguages.includes(lang)) {
      setFormLanguages([...formLanguages, lang]);
    }
  };

  const removeLanguage = (langToRemove: string) => {
    setFormLanguages(formLanguages.filter(lang => lang !== langToRemove));
  };

  const getLanguageLabel = (lang: string) => {
    const languages: { [key: string]: string } = {
      'tr': 'Türkçe',
      'en': 'İngilizce',
      'es': 'İspanyolca',
      'de': 'Almanca',
      'fr': 'Fransızca',
      'it': 'İtalyanca',
      'ru': 'Rusça',
      'ar': 'Arapça',
      'zh': 'Çince',
      'ja': 'Japonca'
    };
    return languages[lang] || lang.toUpperCase();
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Destek Ekibi Yönetimi</h1>
            <p className="text-gray-600 mt-2">
              Destek ekibi üyelerini yönetin ve düzenleyin
            </p>
          </div>
          <button
            onClick={handleNewPerson}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Yeni Destek Kişisi
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Destek</p>
                <p className="text-2xl font-bold text-gray-900">{supportPersons.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Award className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Proje</p>
                <p className="text-2xl font-bold text-gray-900">
                  {supportPersons.reduce((sum, person) => sum + (person.projects || 0), 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ortalama Yanıt</p>
                <p className="text-2xl font-bold text-gray-900">
                  {supportPersons.length > 0 ? 
                    Math.round(supportPersons.reduce((sum, person) => {
                      const time = parseInt(person.repsonseTime?.replace(/[^\d]/g, '') || '0');
                      return sum + time;
                    }, 0) / supportPersons.length) + 'h' : '0h'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Globe className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Dil Desteği</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Array.from(new Set(supportPersons.flatMap(person => person.Languages || []))).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Team Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Destek Ekibi</h2>
          </div>
          
          <div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kişi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bölge & Uzmanlık
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İstatistikler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İletişim
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Diller
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : supportPersons.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Henüz destek kişisi bulunmuyor.
                    </td>
                  </tr>
                ) : supportPersons.map((person) => (
                  <tr key={person._id || person.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center mr-4">
                          {person.profileImage || person.image ? (
                            <img 
                              src={person.profileImage || person.image} 
                              alt="Profile" 
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <User className="h-6 w-6 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {typeof person.name === 'string' ? person.name : (person.name?.tr || 'İsimsiz')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {typeof person.profession === 'string' ? person.profession : (person.profession?.tr || '')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">
                          {typeof person.regions === 'string' ? person.regions : (person.regions?.tr || '')}
                        </div>
                        <div className="text-gray-500">
                          {typeof person.Specialization === 'string' ? person.Specialization : (person.Specialization?.tr || '')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center space-x-4">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                            {person.projects} Proje
                          </span>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                            {person.satisfiedCustomers} Memnun
                          </span>
                        </div>
                        <div className="text-gray-500 mt-1">
                          {typeof person.experience === 'string' ? person.experience : (person.experience?.tr || '')} • {person.repsonseTime}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 text-gray-400 mr-1" />
                          <a href={`mailto:${person.email}`} className="text-primary-600 hover:text-primary-900">
                            {person.email}
                          </a>
                        </div>
                        <div className="flex items-center mt-1">
                          <Phone className="h-4 w-4 text-gray-400 mr-1" />
                          <a href={`tel:${person.phone}`} className="text-gray-600">
                            {person.phone}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(person.Languages || []).map((lang, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                            {getLanguageLabel(lang)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(person)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(person)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Support Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <Users className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPerson ? 'Destek Kişisini Düzenle' : 'Yeni Destek Kişisi'}
                </h2>
              </div>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Language Tabs */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                {(['tr', 'en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Sol Kolon - Çoklu Dil Alanları */}
                  <div className="space-y-6">
                    {/* Bölge */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bölge ({selectedLanguage.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={formRegions[selectedLanguage]}
                        onChange={(e) => setFormRegions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: Avrupa, Asya, Amerika"
                      />
                    </div>

                    {/* İsim */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        İsim ({selectedLanguage.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={formNames[selectedLanguage]}
                        onChange={(e) => setFormNames(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Kişinin adı soyadı"
                        required
                      />
                    </div>

                    {/* Meslek */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meslek ({selectedLanguage.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={formProfessions[selectedLanguage]}
                        onChange={(e) => setFormProfessions(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: Elektrik Mühendisi"
                      />
                    </div>

                    {/* Deneyim */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deneyim ({selectedLanguage.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={formExperiences[selectedLanguage]}
                        onChange={(e) => setFormExperiences(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: 8 yıl, 8 years"
                      />
                    </div>

                    {/* Uzmanlık */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Uzmanlık Alanı ({selectedLanguage.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={formSpecializations[selectedLanguage]}
                        onChange={(e) => setFormSpecializations(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: Alçak Gerilim Panoları"
                      />
                    </div>
                  </div>

                  {/* Sağ Kolon - Diğer Alanlar */}
                  <div className="space-y-6">
                    {/* Profil Fotoğrafı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profil Fotoğrafı
                      </label>
                      <div 
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                        onClick={() => document.getElementById('support-image-input')?.click()}
                      >
                        {imagePreview ? (
                          <div className="relative">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-h-32 mx-auto rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 mb-2">
                              Profil fotoğrafını yükleyin
                            </p>
                            <button
                              type="button"
                              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <Upload className="h-4 w-4 mr-1" />
                              Görsel Seç
                            </button>
                          </>
                        )}
                      </div>
                      <input
                        id="support-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </div>

                    {/* Proje Sayısı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proje Sayısı
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formProjects}
                        onChange={(e) => setFormProjects(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: 12"
                      />
                    </div>

                    {/* Müşteri Memnuniyeti */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Müşteri Memnuniyeti
                      </label>
                      <input
                        type="text"
                        value={formSatisfiedCustomers}
                        onChange={(e) => setFormSatisfiedCustomers(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: 95%"
                      />
                    </div>

                    {/* Yanıt Süresi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yanıt Süresi
                      </label>
                      <input
                        type="text"
                        value={formResponseTime}
                        onChange={(e) => setFormResponseTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Örn: 24h, 12h"
                      />
                    </div>

                    {/* E-posta */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta
                      </label>
                      <input
                        type="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="ornek@email.com"
                        required
                      />
                    </div>

                    {/* Telefon */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        value={formPhone}
                        onChange={(e) => setFormPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0555 000 0000"
                      />
                    </div>
                  </div>
                </div>

                {/* Diller */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Konuştuğu Diller
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formLanguages.map((lang, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                      >
                        {getLanguageLabel(lang)}
                        <button
                          type="button"
                          onClick={() => removeLanguage(lang)}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        addLanguage(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Dil seçin...</option>
                    <option value="tr">Türkçe</option>
                    <option value="en">İngilizce</option>
                    <option value="es">İspanyolca</option>
                    <option value="de">Almanca</option>
                    <option value="fr">Fransızca</option>
                    <option value="it">İtalyanca</option>
                    <option value="ru">Rusça</option>
                    <option value="ar">Arapça</option>
                    <option value="zh">Çince</option>
                    <option value="ja">Japonca</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingPerson ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Support;
